import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { PRODUCT_TYPES } from '../../constants';
import * as XLSX from 'xlsx';
import ProductCategoryNav from './subcomponents/ProductCategoryNav';
import ProductTableHeader from './subcomponents/ProductTableHeader';
import ProductSearchBar from './subcomponents/ProductSearchBar';
import ProductDataTable from './subcomponents/ProductDataTable';
import ProductTypeSettings from './subcomponents/ProductTypeSettings';
import ProductFormModal from './subcomponents/modals/ProductFormModal';
import DeleteConfirmModal from './subcomponents/modals/DeleteConfirmModal';
import ExcelStatusModal from './subcomponents/modals/ExcelStatusModal';
import './ProductList.css';

const ProductList = () => {
  const { isAdmin, isSatisci, isMontajci } = useAuth();
  const { t } = useLanguage();

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [activeTypeTab, setActiveTypeTab] = useState(Object.keys(PRODUCT_TYPES)[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const fileInputRef = useRef(null);

  const [tulExtraPrices, setTulExtraPrices] = useState({
    sPile: '', americanTekli: '', american2: '', american3: '',
    kapsul: '', altEtekKatlama: ''
  });
  const [kanatExtraPrices, setKanatExtraPrices] = useState({
    sPile: '', americanTekli: '', american2: '', american3: '',
    kapsul: ''
  });
  const [jaluziExtras, setJaluziExtras] = useState({
    zigil: '', reduktor: '', kurdela: '', motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [metalJaluziExtras, setMetalJaluziExtras] = useState({
    zigil: '', reduktor: '', ikiCamArasi: '', motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [storExtras, setStorExtras] = useState({
    jumbo: '',
    altEtekDilim: '',
    kapaliKasa: '',
    altEtekLazer: '',
    dekoratifLama: '',
    dekoratifKasa: '',
    altEtekBeyazIs: '',
    motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [zebraExtras, setZebraExtras] = useState({
    jumbo: '',
    altEtekDilim: '',
    kapaliKasa: '',
    motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [dikeyExtras, setDikeyExtras] = useState({
    fon: '', fonList: [], motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [dikeyPvcExtras, setDikeyPvcExtras] = useState({
    motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [dikeyStorExtras, setDikeyStorExtras] = useState({
    motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [katlamaliExtras, setKatlamaliExtras] = useState({
    mekanizmaList: [], motorCurrency: 'EUR', motorPrice: '',
    motorFirma1Name: '', motorFirma1Price: '', motorFirma1KumandaPrice: '',
    motorFirma2Name: '', motorFirma2Price: '', motorFirma2KumandaPrice: '',
    motorFirma3Name: '', motorFirma3Price: '', motorFirma3KumandaPrice: ''
  });
  const [newDikeyFonName, setNewDikeyFonName] = useState('');
  const [newKatlamaliMekanizma, setNewKatlamaliMekanizma] = useState({ type: '', price: '' });
  const [isFonListCollapsed, setIsFonListCollapsed] = useState(true);
  const [editingDikeyFonName, setEditingDikeyFonName] = useState({ index: null, value: '' });
  const [isKatlamaliMekanizmaCollapsed, setIsKatlamaliMekanizmaCollapsed] = useState(true);
  const [rustikExtras, setRustikExtras] = useState({
    ayakModelList: [],
    ayakCapList: [],
    ayakUzunlukList: [],
    ayakRenkKoduList: [],
    ayakPrice: ''
  });
  const [newRustikModel, setNewRustikModel] = useState('');
  const [newRustikCap, setNewRustikCap] = useState('');
  const [newRustikUzunluk, setNewRustikUzunluk] = useState('');
  const [newRustikRenk, setNewRustikRenk] = useState('');
  const [isRustikSettingsCollapsed, setIsRustikSettingsCollapsed] = useState(true);
  const [isRustikModelCollapsed, setIsRustikModelCollapsed] = useState(true);
  const [isRustikCapCollapsed, setIsRustikCapCollapsed] = useState(true);
  const [isRustikUzunlukCollapsed, setIsRustikUzunlukCollapsed] = useState(true);
  const [isRustikRenkCollapsed, setIsRustikRenkCollapsed] = useState(true);

  const [editingRustikModel, setEditingRustikModel] = useState({ index: null, value: '' });
  const [editingRustikCap, setEditingRustikCap] = useState({ index: null, value: '' });
  const [editingRustikUzunluk, setEditingRustikUzunluk] = useState({ index: null, value: '' });
  const [editingRustikRenk, setEditingRustikRenk] = useState({ index: null, value: '' });

  useEffect(() => {
    // Fetch Settings
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTulExtraPrices({
            sPile: data.tulSPilePrice ?? data.sPilePrice ?? '',
            americanTekli: data.tulAmericanTekliPrice ?? data.americanTekliPrice ?? '',
            american2: data.tulAmerican2Price ?? data.american2Price ?? '',
            american3: data.tulAmerican3Price ?? data.american3Price ?? '',
            kapsul: data.tulKapsulPrice !== undefined ? data.tulKapsulPrice : '',
            altEtekKatlama: data.tulAltEtekKatlamaPrice !== undefined ? data.tulAltEtekKatlamaPrice : '',
          });
          setKanatExtraPrices({
            sPile: data.kanatSPilePrice ?? data.sPilePrice ?? '',
            americanTekli: data.kanatAmericanTekliPrice ?? data.americanTekliPrice ?? '',
            american2: data.kanatAmerican2Price ?? data.american2Price ?? '',
            american3: data.kanatAmerican3Price ?? data.american3Price ?? '',
            kapsul: data.kanatKapsulPrice !== undefined ? data.kanatKapsulPrice : '',
          });
          setJaluziExtras({
            zigil: data.zigilPrice || '',
            motorCurrency: data.ahsapJaluziMotorCurrency || 'EUR',
            motorPrice: data.ahsapJaluziMotorPrice || '',
            motorFirma1Name: data.ahsapJaluziMotorFirma1Name ?? '',
            motorFirma1Price: data.ahsapJaluziMotorFirma1Price ?? (data.ahsapJaluziMotorPrice || ''),
            motorFirma1KumandaPrice: data.ahsapJaluziMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.ahsapJaluziMotorFirma2Name ?? '',
            motorFirma2Price: data.ahsapJaluziMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.ahsapJaluziMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.ahsapJaluziMotorFirma3Name ?? '',
            motorFirma3Price: data.ahsapJaluziMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.ahsapJaluziMotorFirma3KumandaPrice ?? ''
          });
          setMetalJaluziExtras({
            zigil: data.metalZigilPrice || '',
            reduktor: data.metalReduktorPrice || '',
            ikiCamArasi: data.ikiCamArasiPrice || '',
            motorCurrency: data.metalJaluziMotorCurrency || 'EUR',
            motorPrice: data.metalJaluziMotorPrice || '',
            motorFirma1Name: data.metalJaluziMotorFirma1Name ?? '',
            motorFirma1Price: data.metalJaluziMotorFirma1Price ?? (data.metalJaluziMotorPrice || ''),
            motorFirma1KumandaPrice: data.metalJaluziMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.metalJaluziMotorFirma2Name ?? '',
            motorFirma2Price: data.metalJaluziMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.metalJaluziMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.metalJaluziMotorFirma3Name ?? '',
            motorFirma3Price: data.metalJaluziMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.metalJaluziMotorFirma3KumandaPrice ?? ''
          });
          setRustikExtras({
            ayakModelList: data.rustikAyakModelList || [],
            ayakCapList: data.rustikAyakCapList || [],
            ayakUzunlukList: data.rustikAyakUzunlukList || [],
            ayakRenkKoduList: data.rustikAyakRenkKoduList || [],
            ayakPrice: data.rustikAyakPrice || ''
          });
          setStorExtras({
            jumbo: data.storJumboPrice || '',
            altEtekDilim: data.storAltEtekDilimPrice || '',
            kapaliKasa: data.storKapaliKasaPrice || '',
            altEtekLazer: data.storAltEtekLazerPrice || '',
            dekoratifLama: data.storDekoratifLamaPrice || '',
            dekoratifKasa: data.storDekoratifKasaPrice || '',
            altEtekBeyazIs: data.storAltEtekBeyazIsPrice || '',
            motorCurrency: data.storMotorCurrency || 'EUR',
            motorPrice: data.storMotorPrice || '',
            motorFirma1Name: data.storMotorFirma1Name ?? '',
            motorFirma1Price: data.storMotorFirma1Price ?? (data.storMotorPrice || ''),
            motorFirma1KumandaPrice: data.storMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.storMotorFirma2Name ?? '',
            motorFirma2Price: data.storMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.storMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.storMotorFirma3Name ?? '',
            motorFirma3Price: data.storMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.storMotorFirma3KumandaPrice ?? ''
          });
          setZebraExtras({
            jumbo: data.zebraJumboPrice || '',
            altEtekDilim: data.zebraAltEtekDilimPrice || '',
            kapaliKasa: data.zebraKapaliKasaPrice || '',
            motorCurrency: data.zebraMotorCurrency || 'EUR',
            motorPrice: data.zebraMotorPrice || '',
            motorFirma1Name: data.zebraMotorFirma1Name ?? '',
            motorFirma1Price: data.zebraMotorFirma1Price ?? (data.zebraMotorPrice || ''),
            motorFirma1KumandaPrice: data.zebraMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.zebraMotorFirma2Name ?? '',
            motorFirma2Price: data.zebraMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.zebraMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.zebraMotorFirma3Name ?? '',
            motorFirma3Price: data.zebraMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.zebraMotorFirma3KumandaPrice ?? ''
          });
          setDikeyExtras({
            fon: data.dikeyFonPrice || '',
            fonList: data.dikeyFonList || [],
            motorCurrency: data.dikeyPerdeMotorCurrency || 'EUR',
            motorPrice: data.dikeyPerdeMotorPrice || '',
            motorFirma1Name: data.dikeyPerdeMotorFirma1Name ?? '',
            motorFirma1Price: data.dikeyPerdeMotorFirma1Price ?? (data.dikeyPerdeMotorPrice || ''),
            motorFirma1KumandaPrice: data.dikeyPerdeMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.dikeyPerdeMotorFirma2Name ?? '',
            motorFirma2Price: data.dikeyPerdeMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.dikeyPerdeMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.dikeyPerdeMotorFirma3Name ?? '',
            motorFirma3Price: data.dikeyPerdeMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.dikeyPerdeMotorFirma3KumandaPrice ?? ''
          });
          setDikeyPvcExtras({
            motorCurrency: data.dikeyPvcMotorCurrency || 'EUR',
            motorPrice: data.dikeyPvcMotorPrice || '',
            motorFirma1Name: data.dikeyPvcMotorFirma1Name ?? '',
            motorFirma1Price: data.dikeyPvcMotorFirma1Price ?? (data.dikeyPvcMotorPrice || ''),
            motorFirma1KumandaPrice: data.dikeyPvcMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.dikeyPvcMotorFirma2Name ?? '',
            motorFirma2Price: data.dikeyPvcMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.dikeyPvcMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.dikeyPvcMotorFirma3Name ?? '',
            motorFirma3Price: data.dikeyPvcMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.dikeyPvcMotorFirma3KumandaPrice ?? ''
          });
          setDikeyStorExtras({
            motorCurrency: data.dikeyStorMotorCurrency || 'EUR',
            motorPrice: data.dikeyStorMotorPrice || '',
            motorFirma1Name: data.dikeyStorMotorFirma1Name ?? '',
            motorFirma1Price: data.dikeyStorMotorFirma1Price ?? (data.dikeyStorMotorPrice || ''),
            motorFirma1KumandaPrice: data.dikeyStorMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.dikeyStorMotorFirma2Name ?? '',
            motorFirma2Price: data.dikeyStorMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.dikeyStorMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.dikeyStorMotorFirma3Name ?? '',
            motorFirma3Price: data.dikeyStorMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.dikeyStorMotorFirma3KumandaPrice ?? ''
          });
          setKatlamaliExtras({
            mekanizmaList: data.katlamaliMekanizmaList || [],
            motorCurrency: data.katlamaliPerdeMotorCurrency || 'EUR',
            motorPrice: data.katlamaliPerdeMotorPrice || '',
            motorFirma1Name: data.katlamaliPerdeMotorFirma1Name ?? '',
            motorFirma1Price: data.katlamaliPerdeMotorFirma1Price ?? (data.katlamaliPerdeMotorPrice || ''),
            motorFirma1KumandaPrice: data.katlamaliPerdeMotorFirma1KumandaPrice ?? '',
            motorFirma2Name: data.katlamaliPerdeMotorFirma2Name ?? '',
            motorFirma2Price: data.katlamaliPerdeMotorFirma2Price ?? '',
            motorFirma2KumandaPrice: data.katlamaliPerdeMotorFirma2KumandaPrice ?? '',
            motorFirma3Name: data.katlamaliPerdeMotorFirma3Name ?? '',
            motorFirma3Price: data.katlamaliPerdeMotorFirma3Price ?? '',
            motorFirma3KumandaPrice: data.katlamaliPerdeMotorFirma3KumandaPrice ?? ''
          });
        }
      } catch (err) {
        console.error('Settings fetch error:', err);
      }
    };
    fetchSettings();
  }, []);

  const handleSaveSettings = async (overrideData = null) => {
    setIsSavingSettings(true);
    try {
      const settingsRef = doc(db, 'settings', 'global');
      let updateData = overrideData || {};

      if (!overrideData) {
        if (activeTypeTab === 'tul') {
          updateData = {
            tulSPilePrice: Number(tulExtraPrices.sPile),
            tulAmericanTekliPrice: Number(tulExtraPrices.americanTekli) || 0,
            tulAmerican2Price: Number(tulExtraPrices.american2),
            tulAmerican3Price: Number(tulExtraPrices.american3),
            tulKapsulPrice: Number(tulExtraPrices.kapsul) || 0,
            tulAltEtekKatlamaPrice: Number(tulExtraPrices.altEtekKatlama) || 0
          };
        } else if (activeTypeTab === 'kanat') {
          updateData = {
            kanatSPilePrice: Number(kanatExtraPrices.sPile),
            kanatAmericanTekliPrice: Number(kanatExtraPrices.americanTekli) || 0,
            kanatAmerican2Price: Number(kanatExtraPrices.american2),
            kanatAmerican3Price: Number(kanatExtraPrices.american3),
            kanatKapsulPrice: Number(kanatExtraPrices.kapsul) || 0
          };
        } else if (activeTypeTab === 'ahsapJaluzi') {
          updateData = {
            ahsapJaluziMotorCurrency: jaluziExtras.motorCurrency,
            ahsapJaluziMotorPrice: Number(jaluziExtras.motorFirma1Price) || Number(jaluziExtras.motorPrice) || '',
            ahsapJaluziMotorFirma1Name: jaluziExtras.motorFirma1Name || '',
            ahsapJaluziMotorFirma1Price: jaluziExtras.motorFirma1Price !== '' ? Number(jaluziExtras.motorFirma1Price) : '',
            ahsapJaluziMotorFirma1KumandaPrice: jaluziExtras.motorFirma1KumandaPrice !== '' ? Number(jaluziExtras.motorFirma1KumandaPrice) : '',
            ahsapJaluziMotorFirma2Name: jaluziExtras.motorFirma2Name || '',
            ahsapJaluziMotorFirma2Price: jaluziExtras.motorFirma2Price !== '' ? Number(jaluziExtras.motorFirma2Price) : '',
            ahsapJaluziMotorFirma2KumandaPrice: jaluziExtras.motorFirma2KumandaPrice !== '' ? Number(jaluziExtras.motorFirma2KumandaPrice) : '',
            ahsapJaluziMotorFirma3Name: jaluziExtras.motorFirma3Name || '',
            ahsapJaluziMotorFirma3Price: jaluziExtras.motorFirma3Price !== '' ? Number(jaluziExtras.motorFirma3Price) : '',
            ahsapJaluziMotorFirma3KumandaPrice: jaluziExtras.motorFirma3KumandaPrice !== '' ? Number(jaluziExtras.motorFirma3KumandaPrice) : '',
            zigilPrice: Number(jaluziExtras.zigil)
          };
        } else if (activeTypeTab === 'metalJaluzi') {
          updateData = {
            metalJaluziMotorCurrency: metalJaluziExtras.motorCurrency,
            metalJaluziMotorPrice: Number(metalJaluziExtras.motorFirma1Price) || Number(metalJaluziExtras.motorPrice) || '',
            metalJaluziMotorFirma1Name: metalJaluziExtras.motorFirma1Name || '',
            metalJaluziMotorFirma1Price: metalJaluziExtras.motorFirma1Price !== '' ? Number(metalJaluziExtras.motorFirma1Price) : '',
            metalJaluziMotorFirma1KumandaPrice: metalJaluziExtras.motorFirma1KumandaPrice !== '' ? Number(metalJaluziExtras.motorFirma1KumandaPrice) : '',
            metalJaluziMotorFirma2Name: metalJaluziExtras.motorFirma2Name || '',
            metalJaluziMotorFirma2Price: metalJaluziExtras.motorFirma2Price !== '' ? Number(metalJaluziExtras.motorFirma2Price) : '',
            metalJaluziMotorFirma2KumandaPrice: metalJaluziExtras.motorFirma2KumandaPrice !== '' ? Number(metalJaluziExtras.motorFirma2KumandaPrice) : '',
            metalJaluziMotorFirma3Name: metalJaluziExtras.motorFirma3Name || '',
            metalJaluziMotorFirma3Price: metalJaluziExtras.motorFirma3Price !== '' ? Number(metalJaluziExtras.motorFirma3Price) : '',
            metalJaluziMotorFirma3KumandaPrice: metalJaluziExtras.motorFirma3KumandaPrice !== '' ? Number(metalJaluziExtras.motorFirma3KumandaPrice) : '',
            metalZigilPrice: Number(metalJaluziExtras.zigil),
            metalReduktorPrice: Number(metalJaluziExtras.reduktor),
            ikiCamArasiPrice: Number(metalJaluziExtras.ikiCamArasi)
          };
        } else if (activeTypeTab === 'stor') {
          updateData = {
            storJumboPrice: Number(storExtras.jumbo),
            storAltEtekDilimPrice: Number(storExtras.altEtekDilim),
            storKapaliKasaPrice: Number(storExtras.kapaliKasa),
            storAltEtekLazerPrice: Number(storExtras.altEtekLazer),
            storDekoratifLamaPrice: Number(storExtras.dekoratifLama),
            storDekoratifKasaPrice: Number(storExtras.dekoratifKasa),
            storAltEtekBeyazIsPrice: Number(storExtras.altEtekBeyazIs),
            storMotorCurrency: storExtras.motorCurrency,
            storMotorPrice: Number(storExtras.motorFirma1Price) || Number(storExtras.motorPrice) || '',
            storMotorFirma1Name: storExtras.motorFirma1Name || '',
            storMotorFirma1Price: storExtras.motorFirma1Price !== '' ? Number(storExtras.motorFirma1Price) : '',
            storMotorFirma1KumandaPrice: storExtras.motorFirma1KumandaPrice !== '' ? Number(storExtras.motorFirma1KumandaPrice) : '',
            storMotorFirma2Name: storExtras.motorFirma2Name || '',
            storMotorFirma2Price: storExtras.motorFirma2Price !== '' ? Number(storExtras.motorFirma2Price) : '',
            storMotorFirma2KumandaPrice: storExtras.motorFirma2KumandaPrice !== '' ? Number(storExtras.motorFirma2KumandaPrice) : '',
            storMotorFirma3Name: storExtras.motorFirma3Name || '',
            storMotorFirma3Price: storExtras.motorFirma3Price !== '' ? Number(storExtras.motorFirma3Price) : '',
            storMotorFirma3KumandaPrice: storExtras.motorFirma3KumandaPrice !== '' ? Number(storExtras.motorFirma3KumandaPrice) : ''
          };
        } else if (activeTypeTab === 'zebra') {
          updateData = {
            zebraJumboPrice: Number(zebraExtras.jumbo),
            zebraAltEtekDilimPrice: Number(zebraExtras.altEtekDilim),
            zebraKapaliKasaPrice: Number(zebraExtras.kapaliKasa),
            zebraMotorCurrency: zebraExtras.motorCurrency,
            zebraMotorPrice: Number(zebraExtras.motorFirma1Price) || Number(zebraExtras.motorPrice) || '',
            zebraMotorFirma1Name: zebraExtras.motorFirma1Name || '',
            zebraMotorFirma1Price: zebraExtras.motorFirma1Price !== '' ? Number(zebraExtras.motorFirma1Price) : '',
            zebraMotorFirma1KumandaPrice: zebraExtras.motorFirma1KumandaPrice !== '' ? Number(zebraExtras.motorFirma1KumandaPrice) : '',
            zebraMotorFirma2Name: zebraExtras.motorFirma2Name || '',
            zebraMotorFirma2Price: zebraExtras.motorFirma2Price !== '' ? Number(zebraExtras.motorFirma2Price) : '',
            zebraMotorFirma2KumandaPrice: zebraExtras.motorFirma2KumandaPrice !== '' ? Number(zebraExtras.motorFirma2KumandaPrice) : '',
            zebraMotorFirma3Name: zebraExtras.motorFirma3Name || '',
            zebraMotorFirma3Price: zebraExtras.motorFirma3Price !== '' ? Number(zebraExtras.motorFirma3Price) : '',
            zebraMotorFirma3KumandaPrice: zebraExtras.motorFirma3KumandaPrice !== '' ? Number(zebraExtras.motorFirma3KumandaPrice) : ''
          };
        } else if (activeTypeTab === 'dikeyPerde') {
          updateData = {
            dikeyFonPrice: Number(dikeyExtras.fon),
            dikeyFonList: dikeyExtras.fonList || [],
            dikeyPerdeMotorCurrency: dikeyExtras.motorCurrency,
            dikeyPerdeMotorPrice: Number(dikeyExtras.motorFirma1Price) || Number(dikeyExtras.motorPrice) || '',
            dikeyPerdeMotorFirma1Name: dikeyExtras.motorFirma1Name || '',
            dikeyPerdeMotorFirma1Price: dikeyExtras.motorFirma1Price !== '' ? Number(dikeyExtras.motorFirma1Price) : '',
            dikeyPerdeMotorFirma1KumandaPrice: dikeyExtras.motorFirma1KumandaPrice !== '' ? Number(dikeyExtras.motorFirma1KumandaPrice) : '',
            dikeyPerdeMotorFirma2Name: dikeyExtras.motorFirma2Name || '',
            dikeyPerdeMotorFirma2Price: dikeyExtras.motorFirma2Price !== '' ? Number(dikeyExtras.motorFirma2Price) : '',
            dikeyPerdeMotorFirma2KumandaPrice: dikeyExtras.motorFirma2KumandaPrice !== '' ? Number(dikeyExtras.motorFirma2KumandaPrice) : '',
            dikeyPerdeMotorFirma3Name: dikeyExtras.motorFirma3Name || '',
            dikeyPerdeMotorFirma3Price: dikeyExtras.motorFirma3Price !== '' ? Number(dikeyExtras.motorFirma3Price) : '',
            dikeyPerdeMotorFirma3KumandaPrice: dikeyExtras.motorFirma3KumandaPrice !== '' ? Number(dikeyExtras.motorFirma3KumandaPrice) : ''
          };
        } else if (activeTypeTab === 'dikeyPvc') {
          updateData = {
            dikeyPvcMotorCurrency: dikeyPvcExtras.motorCurrency,
            dikeyPvcMotorPrice: Number(dikeyPvcExtras.motorFirma1Price) || Number(dikeyPvcExtras.motorPrice) || '',
            dikeyPvcMotorFirma1Name: dikeyPvcExtras.motorFirma1Name || '',
            dikeyPvcMotorFirma1Price: dikeyPvcExtras.motorFirma1Price !== '' ? Number(dikeyPvcExtras.motorFirma1Price) : '',
            dikeyPvcMotorFirma1KumandaPrice: dikeyPvcExtras.motorFirma1KumandaPrice !== '' ? Number(dikeyPvcExtras.motorFirma1KumandaPrice) : '',
            dikeyPvcMotorFirma2Name: dikeyPvcExtras.motorFirma2Name || '',
            dikeyPvcMotorFirma2Price: dikeyPvcExtras.motorFirma2Price !== '' ? Number(dikeyPvcExtras.motorFirma2Price) : '',
            dikeyPvcMotorFirma2KumandaPrice: dikeyPvcExtras.motorFirma2KumandaPrice !== '' ? Number(dikeyPvcExtras.motorFirma2KumandaPrice) : '',
            dikeyPvcMotorFirma3Name: dikeyPvcExtras.motorFirma3Name || '',
            dikeyPvcMotorFirma3Price: dikeyPvcExtras.motorFirma3Price !== '' ? Number(dikeyPvcExtras.motorFirma3Price) : '',
            dikeyPvcMotorFirma3KumandaPrice: dikeyPvcExtras.motorFirma3KumandaPrice !== '' ? Number(dikeyPvcExtras.motorFirma3KumandaPrice) : ''
          };
        } else if (activeTypeTab === 'dikeyStor') {
          updateData = {
            dikeyStorMotorCurrency: dikeyStorExtras.motorCurrency,
            dikeyStorMotorPrice: Number(dikeyStorExtras.motorFirma1Price) || Number(dikeyStorExtras.motorPrice) || '',
            dikeyStorMotorFirma1Name: dikeyStorExtras.motorFirma1Name || '',
            dikeyStorMotorFirma1Price: dikeyStorExtras.motorFirma1Price !== '' ? Number(dikeyStorExtras.motorFirma1Price) : '',
            dikeyStorMotorFirma1KumandaPrice: dikeyStorExtras.motorFirma1KumandaPrice !== '' ? Number(dikeyStorExtras.motorFirma1KumandaPrice) : '',
            dikeyStorMotorFirma2Name: dikeyStorExtras.motorFirma2Name || '',
            dikeyStorMotorFirma2Price: dikeyStorExtras.motorFirma2Price !== '' ? Number(dikeyStorExtras.motorFirma2Price) : '',
            dikeyStorMotorFirma2KumandaPrice: dikeyStorExtras.motorFirma2KumandaPrice !== '' ? Number(dikeyStorExtras.motorFirma2KumandaPrice) : '',
            dikeyStorMotorFirma3Name: dikeyStorExtras.motorFirma3Name || '',
            dikeyStorMotorFirma3Price: dikeyStorExtras.motorFirma3Price !== '' ? Number(dikeyStorExtras.motorFirma3Price) : '',
            dikeyStorMotorFirma3KumandaPrice: dikeyStorExtras.motorFirma3KumandaPrice !== '' ? Number(dikeyStorExtras.motorFirma3KumandaPrice) : ''
          };
        } else if (activeTypeTab === 'katlamaliPerde') {
          updateData = {
            katlamaliMekanizmaList: katlamaliExtras.mekanizmaList || [],
            katlamaliPerdeMotorCurrency: katlamaliExtras.motorCurrency,
            katlamaliPerdeMotorPrice: Number(katlamaliExtras.motorFirma1Price) || Number(katlamaliExtras.motorPrice) || '',
            katlamaliPerdeMotorFirma1Name: katlamaliExtras.motorFirma1Name || '',
            katlamaliPerdeMotorFirma1Price: katlamaliExtras.motorFirma1Price !== '' ? Number(katlamaliExtras.motorFirma1Price) : '',
            katlamaliPerdeMotorFirma1KumandaPrice: katlamaliExtras.motorFirma1KumandaPrice !== '' ? Number(katlamaliExtras.motorFirma1KumandaPrice) : '',
            katlamaliPerdeMotorFirma2Name: katlamaliExtras.motorFirma2Name || '',
            katlamaliPerdeMotorFirma2Price: katlamaliExtras.motorFirma2Price !== '' ? Number(katlamaliExtras.motorFirma2Price) : '',
            katlamaliPerdeMotorFirma2KumandaPrice: katlamaliExtras.motorFirma2KumandaPrice !== '' ? Number(katlamaliExtras.motorFirma2KumandaPrice) : '',
            katlamaliPerdeMotorFirma3Name: katlamaliExtras.motorFirma3Name || '',
            katlamaliPerdeMotorFirma3Price: katlamaliExtras.motorFirma3Price !== '' ? Number(katlamaliExtras.motorFirma3Price) : '',
            katlamaliPerdeMotorFirma3KumandaPrice: katlamaliExtras.motorFirma3KumandaPrice !== '' ? Number(katlamaliExtras.motorFirma3KumandaPrice) : ''
          };
        } else if (activeTypeTab === 'rustik') {
          updateData = {
            rustikAyakModelList: rustikExtras.ayakModelList || [],
            rustikAyakCapList: rustikExtras.ayakCapList || [],
            rustikAyakUzunlukList: rustikExtras.ayakUzunlukList || [],
            rustikAyakRenkKoduList: rustikExtras.ayakRenkKoduList || [],
            rustikAyakPrice: rustikExtras.ayakPrice || ''
          };
        }
      }

      await setDoc(settingsRef, updateData, { merge: true });

      if (!overrideData || activeTypeTab === 'rustik') {
        setExcelModal({
          show: true,
          type: 'success',
          title: t('common.success', 'Başarılı'),
          message: t('productList.modal.settingsSaved', 'Ayarlar başarıyla kaydedildi.')
        });
      }
    } catch (err) {
      console.error('Settings save error:', err);
      if (!overrideData) {
        setExcelModal({
          show: true,
          type: 'error',
          title: t('common.error', 'Hata'),
          message: t('productList.modal.settingsSaveError', 'Ayarlar kaydedilirken bir hata oluştu.')
        });
      }
    }
    setIsSavingSettings(false);
  };

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    productType: Object.keys(PRODUCT_TYPES)[0],
    currency: 'EUR',
    unitPrice: '',
    // Jaluziler için ekstra fiyatlandırma
    merdivenIpliFiyat: '',
    kurdeleliFiyat: '',
    merdivenIpliReduktorluFiyat: '',
    kurdelaliReduktorluFiyat: ''
  });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Silme modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [excelModal, setExcelModal] = useState({ show: false, type: '', title: '', message: '', onConfirm: null });

  // Ürünleri Firestore'dan çek
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'productItems'));
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      // İsme göre sırala
      data.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setProducts(data);
    } catch (error) {
      console.error('Ürünler yüklenirken hata:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Sekme değişince arama kutusunu sıfırla
  const handleTabChange = (key) => {
    setActiveTypeTab(key);
    setSearchQuery('');
  };

  // Aktif sekmedeki ürünler (arama filtreli)
  const filteredProducts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return products
      .filter(p => {
        if (p.productType !== activeTypeTab) return false;
        if (!query) return true;
        return (p.name || '').toLowerCase().includes(query);
      })
      .map(p => ({
        ...p,
        currency: 'EUR'
      }));
  }, [products, activeTypeTab, searchQuery]);

  // Form aç - yeni ürün
  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      productType: activeTypeTab,
      currency: 'EUR',
      unitPrice: '',
      merdivenIpliFiyat: '',
      kurdeleliFiyat: '',
      merdivenIpliReduktorluFiyat: '',
      kurdelaliReduktorluFiyat: ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form aç - düzenle
  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      productType: product.productType || activeTypeTab,
      currency: 'EUR',
      unitPrice: product.unitPrice !== undefined ? product.unitPrice : '',
      merdivenIpliFiyat: product.merdivenIpliFiyat !== undefined ? product.merdivenIpliFiyat : '',
      kurdeleliFiyat: product.kurdeleliFiyat !== undefined ? product.kurdeleliFiyat : '',
      merdivenIpliReduktorluFiyat: product.merdivenIpliReduktorluFiyat !== undefined ? product.merdivenIpliReduktorluFiyat : '',
      kurdelaliReduktorluFiyat: product.kurdelaliReduktorluFiyat !== undefined ? product.kurdelaliReduktorluFiyat : ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form kapat
  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormError(null);
  };

  // Kaydet
  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!formData.name.trim()) {
      setFormError('Ürün adı zorunludur.');
      return;
    }

    const isJaluzi = formData.productType === 'ahsapJaluzi';
    const isRustik = formData.productType === 'rustik';

    if (!isJaluzi && !isRustik && (!formData.unitPrice || isNaN(parseFloat(formData.unitPrice)))) {
      setFormError('Birim fiyat geçerli bir sayı olmalıdır.');
      return;
    }

    // Jaluziler için en az bir baz fiyat gerekli mi diye kontrol edilebilir, opsiyonel bırakıyoruz/esnek.

    setIsSaving(true);
    try {
      let payload = {
        name: formData.name.trim(),
        productType: formData.productType,
        currency: 'EUR',
        updatedAt: new Date()
      };

      if (isJaluzi) {
        payload = {
          ...payload,
          merdivenIpliFiyat: formData.merdivenIpliFiyat ? parseFloat(formData.merdivenIpliFiyat) : 0,
          kurdeleliFiyat: formData.kurdeleliFiyat ? parseFloat(formData.kurdeleliFiyat) : 0,
          merdivenIpliReduktorluFiyat: formData.merdivenIpliReduktorluFiyat ? parseFloat(formData.merdivenIpliReduktorluFiyat) : 0,
          kurdelaliReduktorluFiyat: formData.kurdelaliReduktorluFiyat ? parseFloat(formData.kurdelaliReduktorluFiyat) : 0
        };
      } else if (isRustik) {
        payload = {
          ...payload,
          unitPrice: 0
        };
      } else {
        payload = {
          ...payload,
          unitPrice: parseFloat(formData.unitPrice)
        };
      }

      if (editingProduct) {
        await updateDoc(doc(db, 'productItems', editingProduct.id), payload);
        setFormSuccess('Ürün başarıyla güncellendi!');
      } else {
        await addDoc(collection(db, 'productItems'), {
          ...payload,
          createdAt: new Date()
        });
        setFormSuccess('Ürün başarıyla eklendi!');
      }

      await fetchProducts();
      // Yeni ürün eklenince o tipin sekmesine geç
      setActiveTypeTab(formData.productType);

      setTimeout(() => {
        setShowForm(false);
        setEditingProduct(null);
        setFormSuccess(null);
      }, 1200);
    } catch (error) {
      console.error('Ürün kaydedilirken hata:', error);
      setFormError('Ürün kaydedilirken bir hata oluştu: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Silme
  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteDoc(doc(db, 'productItems', deleteModal.id));
      setDeleteModal(null);
      await fetchProducts();
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
    }
  };

  // Excel'e Aktar
  const handleExportExcel = () => {
    if (filteredProducts.length === 0) {
      setExcelModal({
        show: true,
        type: 'info',
        title: t('common.info', 'Bilgi'),
        message: t('productList.excel.noProductsToExport', 'Dışa aktarılacak ürün bulunamadı.')
      });
      return;
    }

    const isJaluzi = activeTypeTab === 'ahsapJaluzi';

    const exportData = filteredProducts.map(p => {
      if (isJaluzi) {
        return {
          'Ürün Adı': p.name,
          'Merdiven İpli (€)': p.merdivenIpliFiyat || 0,
          'Kurdeleli (€)': p.kurdeleliFiyat || 0,
          'Merdiven İpli Redüktörlü (€)': p.merdivenIpliReduktorluFiyat || 0,
          'Kurdelalı Redüktörlü (€)': p.kurdelaliReduktorluFiyat || 0
        };
      } else {
        return {
          'Ürün Adı': p.name,
          'Birim Fiyat (€)': p.unitPrice || 0
        };
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ürünler');

    const typeLabel = t(`productTypes.${activeTypeTab}`, PRODUCT_TYPES[activeTypeTab]?.label || 'Urunler');
    XLSX.writeFile(workbook, `${typeLabel}_Urun_Listesi.xlsx`);
  };

  // Excel'den Yükle butonu tıklaması
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Listeyi Temizle
  const handleClearList = () => {
    const typeLabel = t(`productTypes.${activeTypeTab}`, PRODUCT_TYPES[activeTypeTab]?.label);
    const currentCount = products.filter(p => p.productType === activeTypeTab).length;
    if (currentCount === 0) {
      setExcelModal({
        show: true,
        type: 'info',
        title: t('common.info', 'Bilgi'),
        message: t('productList.excel.noProductsToDelete', 'Bu kategoride silinecek ürün bulunamadı.')
      });
      return;
    }
    setExcelModal({
      show: true,
      type: 'confirm',
      title: t('productList.excel.clearConfirmTitle', 'Listeyi Temizle'),
      message: t('productList.excel.clearConfirmMessage', `DİKKAT! "${typeLabel}" kategorisindeki ${currentCount} ürünün TAMAMI silinecektir.\n\nBu işlem geri alınamaz. Emin misiniz?`, { type: typeLabel, count: currentCount }),
      onConfirm: async () => {
        setExcelModal({ show: false });
        setIsExcelLoading(true);
        try {
          const toDelete = products.filter(p => p.productType === activeTypeTab);

          // Chunked batch deletion (Firebase 500 limit koruması)
          const CHUNK_SIZE = 400;
          for (let i = 0; i < toDelete.length; i += CHUNK_SIZE) {
            const chunk = toDelete.slice(i, i + CHUNK_SIZE);
            const batch = writeBatch(db);
            chunk.forEach(p => batch.delete(doc(db, 'productItems', p.id)));
            await batch.commit();
          }

          setExcelModal({
            show: true,
            type: 'success',
            title: t('productList.excel.deletedTitle', 'Temizlendi'),
            message: t('productList.excel.deletedCount', `${toDelete.length} ürün başarıyla silindi.`, { count: toDelete.length })
          });
          await fetchProducts();
        } catch (err) {
          setExcelModal({
            show: true,
            type: 'error',
            title: t('common.error', 'Hata'),
            message: t('productList.excel.clearError', 'Liste temizlenirken bir hata oluştu: ') + err.message
          });
        } finally {
          setIsExcelLoading(false);
        }
      }
    });
  };

  // Excel'den Dataları Veritabanına Yükle — Upsert mantığı (ada göre eşleştir)
  const processExcelData = async (data, typeLabel) => {
    setExcelModal({ show: false });
    try {
      const isJaluzi = activeTypeTab === 'ahsapJaluzi';

      // Mevcut ürünleri normalize edilmiş ada göre map'le
      const existingByName = {};
      products
        .filter(p => p.productType === activeTypeTab)
        .forEach(p => {
          existingByName[(p.name || '').toLowerCase().trim()] = p;
        });

      const CHUNK_SIZE = 400;
      let operations = [];
      let updateCount = 0;
      let addCount = 0;

      for (const row of data) {
        const name = row['Ürün Adı']?.toString().trim();
        if (!name) continue;
        let payload = {
          name,
          productType: activeTypeTab,
          currency: 'EUR',
          updatedAt: new Date()
        };

        let isValid = false;

        if (isJaluzi) {
          payload = {
            ...payload,
            merdivenIpliFiyat: parseFloat(row['Merdiven İpli (€)'] || row['Merdiven İpli']) || 0,
            kurdeleliFiyat: parseFloat(row['Kurdeleli (€)'] || row['Kurdeleli']) || 0,
            merdivenIpliReduktorluFiyat: parseFloat(row['Merdiven İpli Redüktörlü (€)'] || row['Merdiven İpli Redüktörlü']) || 0,
            kurdelaliReduktorluFiyat: parseFloat(row['Kurdelalı Redüktörlü (€)'] || row['Kurdelalı Redüktörlü']) || 0
          };
          isValid = true;
        } else {
          const unitPrice = parseFloat(row['Birim Fiyat (€)'] || row['Birim Fiyat']);
          if (!isNaN(unitPrice)) {
            payload = { ...payload, unitPrice };
            isValid = true;
          }
        }

        if (!isValid) continue;

        const existing = existingByName[name.toLowerCase()];
        if (existing) {
          // Mevcut ürünü güncelle
          operations.push((b) => b.update(doc(db, 'productItems', existing.id), payload));
          updateCount++;
        } else {
          // Yeni ürün ekle
          const newRef = doc(collection(db, 'productItems'));
          const createdAt = new Date();
          operations.push((b) => b.set(newRef, { ...payload, createdAt }));
          addCount++;
        }
      }

      // Chunklar halinde commit et
      for (let i = 0; i < operations.length; i += CHUNK_SIZE) {
        const chunk = operations.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        for (const op of chunk) {
          op(batch);
        }
        await batch.commit();
      }

      const msg = [
        updateCount > 0 ? t('productList.excel.updatedCount', `${updateCount} ürün güncellendi`, { count: updateCount }) : null,
        addCount > 0 ? t('productList.excel.addedCount', `${addCount} yeni ürün eklendi`, { count: addCount }) : null
      ].filter(Boolean).join(', ');

      setExcelModal({
        show: true,
        type: 'success',
        title: t('common.success', 'Başarılı'),
        message: msg ? `${msg}.` : t('productList.excel.noChanges', 'Değişiklik yapılmadı.')
      });
      await fetchProducts();
    } catch (error) {
      console.error('Excel içe aktarılırken hata:', error);
      setExcelModal({
        show: true,
        type: 'error',
        title: t('common.error', 'Hata'),
        message: t('productList.excel.readError', 'Excel dosyası okunurken bir hata oluştu: ') + error.message
      });
    } finally {
      setIsExcelLoading(false);
    }
  };

  // Excel Dosya Seçimi ve İşlenmesi
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // input değerini sıfırlayalım ki aynı dosyayı bir daha seçebilsin
    e.target.value = null;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setIsExcelLoading(true);
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (data.length === 0) {
          setExcelModal({
            show: true,
            type: 'error',
            title: t('common.error', 'Hata'),
            message: t('productList.excel.emptyOrUnreadable', 'Excel dosyası boş veya okunamadı.')
          });
          setIsExcelLoading(false);
          return;
        }

        // Başlık kontrolü
        const firstRow = data[0];
        const isJaluzi = activeTypeTab === 'ahsapJaluzi';

        if (isJaluzi) {
          if (!('Ürün Adı' in firstRow) || !('Merdiven İpli' in firstRow)) {
            setExcelModal({
              show: true,
              type: 'error',
              title: t('productList.excel.invalidFormatTitle', 'Hatalı Format'),
              message: t('productList.excel.invalidFormatJaluziMsg', `Hatalı Excel formatı! Ahşap/Metal Jaluzi dosyalarında "Ürün Adı", "Merdiven İpli", "Kurdeleli" gibi başlıklar bulunmalıdır.\n\n(Lütfen taslağı görmek için önce "Excel'e Aktar" diyerek örnek dosyayı indirin)`)
            });
            setIsExcelLoading(false);
            return;
          }
        } else {
          if (!('Ürün Adı' in firstRow) || !('Birim Fiyat' in firstRow)) {
            setExcelModal({
              show: true,
              type: 'error',
              title: t('productList.excel.invalidFormatTitle', 'Hatalı Format'),
              message: t('productList.excel.invalidFormatMsg', `Hatalı Excel formatı! Dosyada "Ürün Adı" ve "Birim Fiyat" başlıkları bulunmalıdır.\n\n(Lütfen taslağı görmek için önce "Excel'e Aktar" diyerek örnek dosyayı indirin)`)
            });
            setIsExcelLoading(false);
            return;
          }
        }

        // Upsert onayı — ID'ler korunur, yalnızca güncelleme/ekleme yapılır
        const typeLabel = t(`productTypes.${activeTypeTab}`, PRODUCT_TYPES[activeTypeTab]?.label);
        const existingCount = products.filter(p => p.productType === activeTypeTab).length;
        setExcelModal({
          show: true,
          type: 'confirm',
          title: t('productList.excel.importConfirmTitle', 'Excel Yükleme Onayı'),
          message: t('productList.excel.importConfirmMsg', `Excel dosyasındaki ${data.length} ürün işlenecek:\n\n• Mevcut ${existingCount} ürün içinden eşleşenler güncellenir\n• Listede olmayan yeni ürünler eklenir\n• Listede olup Excel'de olmayan ürünlere dokunulmaz\n\nOnaylıyor musunuz?`, { count: data.length, existing: existingCount }),
          onConfirm: () => processExcelData(data, typeLabel)
        });
      } catch (error) {
        console.error('Excel dosyası parse edilirken hata:', error);
        setExcelModal({
          show: true,
          type: 'error',
          title: t('productList.excel.parseErrorTitle', 'Parse Hatası'),
          message: t('productList.excel.parseErrorMsg', 'Dosya okunurken bir hata oluştu.')
        });
        setIsExcelLoading(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <div className="product-list-page">
      <div className="product-list-header">
        <div className="product-list-title">
          <h2>{t('productList.title', 'Ürün Listesi')}</h2>
          <p>{t('productList.subtitle', 'Ürün tipine göre kategorize edilmiş ürün kataloğu')}</p>
        </div>
      </div>




      {/* Ürün Tipi Navigasyon */}
      <ProductCategoryNav
        activeTypeTab={activeTypeTab}
        handleTabChange={handleTabChange}
        isMontajci={isMontajci}
        products={products}
        isLoading={isLoading}
      />

      <div className="product-table-wrapper">
        <ProductTableHeader
          activeTypeTab={activeTypeTab}
          filteredProductsCount={filteredProducts.length}
          isAdmin={isAdmin}
          isSatisci={isSatisci}
          isMontajci={isMontajci}
          isExcelLoading={isExcelLoading}
          fileInputRef={fileInputRef}
          handleImportExcel={handleImportExcel}
          handleImportClick={handleImportClick}
          handleExportExcel={handleExportExcel}
          handleClearList={handleClearList}
        />

        <ProductTypeSettings
          activeTypeTab={activeTypeTab}
          isAdmin={isAdmin}
          isSatisci={isSatisci}
          handleSaveSettings={handleSaveSettings}
          isSavingSettings={isSavingSettings}
          rustikExtras={rustikExtras}
          setRustikExtras={setRustikExtras}
          rustikStates={{
            isRustikSettingsCollapsed, setIsRustikSettingsCollapsed,
            isRustikModelCollapsed, setIsRustikModelCollapsed,
            isRustikCapCollapsed, setIsRustikCapCollapsed,
            isRustikUzunlukCollapsed, setIsRustikUzunlukCollapsed,
            isRustikRenkCollapsed, setIsRustikRenkCollapsed,
            newRustikModel, setNewRustikModel,
            newRustikCap, setNewRustikCap,
            newRustikUzunluk, setNewRustikUzunluk,
            newRustikRenk, setNewRustikRenk,
            editingRustikModel, setEditingRustikModel,
            editingRustikCap, setEditingRustikCap,
            editingRustikUzunluk, setEditingRustikUzunluk,
            editingRustikRenk, setEditingRustikRenk
          }}
          tulExtraPrices={tulExtraPrices}
          setTulExtraPrices={setTulExtraPrices}
          kanatExtraPrices={kanatExtraPrices}
          setKanatExtraPrices={setKanatExtraPrices}
          jaluziExtras={jaluziExtras}
          setJaluziExtras={setJaluziExtras}
          metalJaluziExtras={metalJaluziExtras}
          setMetalJaluziExtras={setMetalJaluziExtras}
          storExtras={storExtras}
          setStorExtras={setStorExtras}
          zebraExtras={zebraExtras}
          setZebraExtras={setZebraExtras}
          dikeyExtras={dikeyExtras}
          setDikeyExtras={setDikeyExtras}
          dikeyPvcExtras={dikeyPvcExtras}
          setDikeyPvcExtras={setDikeyPvcExtras}
          dikeyStorExtras={dikeyStorExtras}
          setDikeyStorExtras={setDikeyStorExtras}
          katlamaliExtras={katlamaliExtras}
          setKatlamaliExtras={setKatlamaliExtras}
          katlamaliStates={{
            isKatlamaliMekanizmaCollapsed, setIsKatlamaliMekanizmaCollapsed,
            newKatlamaliMekanizma, setNewKatlamaliMekanizma
          }}
          dikeyStates={{
            isFonListCollapsed, setIsFonListCollapsed,
            newDikeyFonName, setNewDikeyFonName,
            editingDikeyFonName, setEditingDikeyFonName
          }}
        />

        <ProductSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredProductsCount={filteredProducts.length}
          isAdmin={isAdmin}
          isSatisci={isSatisci}
          isMontajci={isMontajci}
          handleAddNew={handleAddNew}
        />

        {isLoading ? (
          <div className="product-loading">
            <i className="fas fa-spinner fa-spin"></i> {t('common.loading', 'Yükleniyor...')}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="product-empty">
            <div className="product-empty-icon-wrap">
              <i className="fas fa-box-open"></i>
            </div>
            <p>{t('productList.emptyCategory', 'Bu kategoride henüz ürün yok.')}</p>
            {(isAdmin || isSatisci) && !isMontajci && (
              <button className="btn-add-product-empty" onClick={handleAddNew}>
                <i className="fas fa-plus"></i> {t('productList.addFirstProduct', 'İlk ürünü ekle')}
              </button>
            )}
          </div>
        ) : (
          <ProductDataTable
            activeTypeTab={activeTypeTab}
            filteredProducts={filteredProducts}
            isAdmin={isAdmin}
            isSatisci={isSatisci}
            isMontajci={isMontajci}
            handleEdit={handleEdit}
            setDeleteModal={setDeleteModal}
          />
        )}
      </div>

      {/* Ürün Ekleme/Düzenleme Formu Modal */}
      <ProductFormModal
        showForm={showForm}
        formData={formData}
        setFormData={setFormData}
        editingProduct={editingProduct}
        isSaving={isSaving}
        formError={formError}
        formSuccess={formSuccess}
        handleSave={handleSave}
        handleCancel={handleCancel}
      />

      {/* Silme Onay Modal */}
      <DeleteConfirmModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        handleDelete={handleDelete}
      />

      {/* Excel İşlemleri Modal */}
      <ExcelStatusModal
        excelModal={excelModal}
        setExcelModal={setExcelModal}
        setIsExcelLoading={setIsExcelLoading}
      />
    </div>
  );
};

export default ProductList;
